import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, LineChart as LineChartIcon, History } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend,
} from "recharts";

const fmtMoney = (v) => new Intl.NumberFormat("en-AU", {
  style: "currency", currency: "AUD", maximumFractionDigits: 0,
}).format(v || 0);

/**
 * Draws a simple trend chart of snapshot totals over the last N days
 * for one of the finance-holdings kinds. Nothing renders while there's
 * fewer than 2 datapoints — a single snapshot doesn't make a trend.
 */
export default function TrendChart({ kind, days = 90, series }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/finance-holdings/history/${kind}`, { params: { days } })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData({ series: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [kind, days]);

  const points = data?.series || [];
  const dataKeys = useMemo(() => {
    if (!series) return [];
    return series.filter((s) => points.some((p) => p[s.key] !== undefined));
  }, [series, points]);

  if (loading && !data) {
    return (
      <Card className="border-zinc-200">
        <CardContent className="p-6 text-center text-zinc-400 text-xs">
          <Loader2 className="h-4 w-4 mx-auto animate-spin mb-2" />Loading trend…
        </CardContent>
      </Card>
    );
  }
  if (points.length < 2) {
    return (
      <Card className="border-dashed border-zinc-300"
        data-testid={`trend-chart-empty-${kind}`}>
        <CardContent className="p-5 text-xs text-zinc-500">
          <div className="flex items-center gap-1.5 font-semibold text-zinc-600 mb-1">
            <History className="h-3.5 w-3.5" />Trend not available yet
          </div>
          <div>
            The nightly pre-warm job has {points.length === 0 ? "not yet" : "only just"} started
            capturing snapshots for this dataset. Come back tomorrow to see a
            {" "}{days}-day trend appear here automatically.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-zinc-200" data-testid={`trend-chart-${kind}`}>
      <CardContent className="p-4">
        <div className="text-xs font-semibold mb-2 flex items-center gap-1.5">
          <LineChartIcon className="h-3.5 w-3.5 text-zinc-500" />
          {days}-day trend · {points.length} snapshot{points.length === 1 ? "" : "s"}
        </div>
        <div style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer>
            <LineChart data={points}
              margin={{ top: 6, right: 16, bottom: 4, left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }}
                tickFormatter={(iso) => new Date(iso).toLocaleDateString("en-AU", { month: "short", day: "numeric" })}
                minTickGap={30} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtMoney} width={90} />
              <Tooltip formatter={fmtMoney} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {dataKeys.map((s) => (
                <Line key={s.key} type="monotone" dataKey={s.key}
                  name={s.label} stroke={s.color}
                  strokeWidth={s.width || 2} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
